import { FC } from "react";

/**
 * Анимированная SVG-иллюстрация для шапки «Публичная оферта»:
 * договор со стопкой листов, печатью, «рисующейся» подписью и
 * перьевой ручкой. Только transform/opacity + dashoffset — дёшево
 * для GPU; при prefers-reduced-motion всё замирает.
 */
export const OfferIllustration: FC<{ className?: string }> = ({ className }) => (
  <div className={className} aria-hidden>
    <style>{`
      .of-g{transform-box:fill-box;transform-origin:center}
      @keyframes of-float{0%,100%{transform:translateY(0)}50%{transform:translateY(-8px)}}
      @keyframes of-float2{0%,100%{transform:translateY(0) rotate(0)}50%{transform:translateY(-7px) rotate(-4deg)}}
      @keyframes of-pulse{0%,100%{transform:scale(1);opacity:.9}50%{transform:scale(1.07);opacity:1}}
      @keyframes of-draw{0%{stroke-dashoffset:210}55%{stroke-dashoffset:0}100%{stroke-dashoffset:0}}
      @keyframes of-twinkle{0%,100%{opacity:.12;transform:scale(.7)}50%{opacity:.65;transform:scale(1)}}
      @keyframes of-orbit{to{transform:rotate(360deg)}}
      .of-doc{animation:of-float 6s ease-in-out infinite}
      .of-seal{animation:of-pulse 3.2s ease-in-out infinite}
      .of-pen{animation:of-float2 5s ease-in-out .4s infinite}
      .of-check{animation:of-float2 6.5s ease-in-out 1.1s infinite}
      .of-sign{stroke-dasharray:210;animation:of-draw 4.5s ease-in-out infinite}
      .of-ring{animation:of-orbit 46s linear infinite}
      .of-t1{animation:of-twinkle 3s ease-in-out infinite}
      .of-t2{animation:of-twinkle 3.7s ease-in-out 1s infinite}
      .of-t3{animation:of-twinkle 3.3s ease-in-out 2s infinite}
      @media (prefers-reduced-motion:reduce){
        .of-doc,.of-seal,.of-pen,.of-check,.of-ring,.of-t1,.of-t2,.of-t3{animation:none}
        .of-sign{animation:none;stroke-dashoffset:0}
      }
    `}</style>

    <svg viewBox="0 0 240 240" fill="none" className="h-full w-full">
      <defs>
        <radialGradient id="of-glow" cx="50%" cy="46%" r="52%">
          <stop offset="0%" stopColor="rgba(208,237,28,0.14)" />
          <stop offset="60%" stopColor="rgba(208,237,28,0.04)" />
          <stop offset="100%" stopColor="rgba(208,237,28,0)" />
        </radialGradient>
        <linearGradient id="of-paper" x1="0" y1="0" x2="0.35" y2="1">
          <stop offset="0%" stopColor="#f9f6ee" />
          <stop offset="100%" stopColor="#e5e0d3" />
        </linearGradient>
        <linearGradient id="of-barrel" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#f7f4ec" />
          <stop offset="100%" stopColor="#d9d3c4" />
        </linearGradient>
      </defs>

      {/* свечение */}
      <circle cx="120" cy="118" r="108" fill="url(#of-glow)" />

      {/* пунктирная орбита */}
      <g className="of-g of-ring">
        <circle
          cx="120" cy="118" r="98"
          stroke="rgba(242,239,232,0.16)" strokeWidth="1.4"
          strokeDasharray="2 12" strokeLinecap="round"
        />
      </g>

      {/* документ */}
      <g className="of-g of-doc">
        {/* лист-подложка (стопка) */}
        <rect x="74" y="42" width="104" height="150" rx="13" fill="#d8d2c4" transform="rotate(5 120 120)" />

        <g transform="rotate(-6 120 120)">
          {/* тень */}
          <rect x="66" y="52" width="112" height="150" rx="14" fill="rgba(0,0,0,0.26)" />
          {/* лист */}
          <rect x="62" y="46" width="112" height="150" rx="14" fill="url(#of-paper)" />
          {/* шапка документа */}
          <circle cx="150" cy="64" r="4" fill="rgba(48,42,65,0.16)" />
          <circle cx="162" cy="64" r="4" fill="rgba(48,42,65,0.1)" />

          {/* строки текста */}
          <rect x="78" y="84" width="48" height="9" rx="4.5" fill="rgba(48,42,65,0.34)" />
          <g fill="rgba(48,42,65,0.15)">
            <rect x="78" y="104" width="80" height="6" rx="3" />
            <rect x="78" y="117" width="64" height="6" rx="3" />
            <rect x="78" y="130" width="72" height="6" rx="3" />
            <rect x="78" y="143" width="52" height="6" rx="3" />
          </g>

          {/* подпись (рисуется) */}
          <path
            className="of-sign"
            d="M80 168c6-11 12 6 19-3s9-13 17-6 15 3 24-5"
            stroke="#302a41" strokeWidth="3.2" strokeLinecap="round" fill="none"
            opacity="0.8"
          />
          <rect x="80" y="181" width="58" height="4" rx="2" fill="rgba(48,42,65,0.14)" />

          {/* печать с лентой */}
          <g className="of-g of-seal">
            <g transform="translate(150 168)">
              <path d="M-6 14l-3 16 9-6 9 6-3-16Z" fill="#a8c215" />
              <circle r="20" fill="#302a41" />
              <circle r="20" stroke="rgba(208,237,28,0.55)" strokeWidth="1.6" strokeDasharray="2 4" />
              <circle r="13" stroke="#d0ed1c" strokeWidth="1.6" fill="none" />
              <path d="M-6 0l4 5 8-9" stroke="#d0ed1c" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
            </g>
          </g>
        </g>
      </g>

      {/* перьевая ручка (целится в документ) */}
      <g className="of-g of-pen">
        <g transform="translate(150 52) rotate(28)">
          {/* тень */}
          <rect x="14" y="-4" width="54" height="10" rx="5" fill="rgba(0,0,0,0.18)" transform="translate(2 4)" />
          {/* перо */}
          <path d="M0 0l16-6v12Z" fill="#d0ed1c" />
          <path d="M5 0h10" stroke="#302a41" strokeWidth="1.5" strokeLinecap="round" />
          <circle cx="13" cy="0" r="1.6" fill="#302a41" />
          {/* хват */}
          <rect x="16" y="-6" width="8" height="12" rx="2" fill="#302a41" />
          {/* корпус */}
          <rect x="24" y="-6" width="44" height="12" rx="6" fill="url(#of-barrel)" />
          {/* лаймовое кольцо */}
          <rect x="26" y="-6" width="5" height="12" fill="#d0ed1c" />
          {/* клипса */}
          <rect x="44" y="-6" width="3" height="15" rx="1.5" fill="#a8c215" />
          {/* торец-крышка */}
          <circle cx="63" cy="0" r="3.4" fill="#302a41" />
        </g>
      </g>

      {/* галочка-бабл */}
      <g className="of-g of-check">
        <g transform="translate(48 76)">
          <circle r="15" fill="#d0ed1c" />
          <path d="M-6 0l4 5 8-9" stroke="#302a41" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        </g>
      </g>

      {/* искры */}
      <g stroke="#d0ed1c" strokeWidth="2.4" strokeLinecap="round">
        <g className="of-g of-t1"><path d="M40 150v11M34.5 155.5h11" /></g>
        <g className="of-g of-t2"><path d="M198 128v10M193 133h10" /></g>
        <g className="of-g of-t3"><path d="M186 190v9M181.5 194.5h9" /></g>
      </g>
    </svg>
  </div>
);
